import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Autocomplete, TextField, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { TSearchAddCategory } from '../../types/global/forms.types';
import { CastingDirectorCard } from './CastingDirectorCard';
import { ICastingDirector } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { getCastingDirectors } from '../../redux/audition/actions';
import { useSelector } from 'react-redux';
import { castingDirectorSelector } from './selector';

type SearchSelectComponentProps = {
    onChange: (newValue: any) => void;
    onAddClick: () => void;
    placeholder?: string;
    sx?: object;
    multiple?: boolean;
    renderer?: React.ReactElement;
    category?: TSearchAddCategory;
    initialValue?: any;
};

/**
 *
 * Casting Director here also means company
 * so use this component as a company
 */

const SearchSelectComponent: React.FC<SearchSelectComponentProps> = ({
    onChange,
    onAddClick,
    placeholder = 'Search or select...',
    sx = {},
    category,
    initialValue = null,
}) => {
    const { themeStyle } = useThemeContext();
    const [selected, setSelected] = useState<ICastingDirector | null>(initialValue);
    const dispatch = useDispatch();
    let value: ICastingDirector[] = [];
    const castingDirectors = useSelector(castingDirectorSelector);

    if (category === 'castingDirector' || category === 'company') {
        value = castingDirectors;
    }

    const handleOnChange = (newValue: ICastingDirector | null) => {
        setSelected(newValue);
        onChange && onChange(newValue);
    };

    const handleCancelClick = () => {
        setSelected(null);
    };

    const getValue = useCallback(() => {
        switch (category) {
            case 'castingDirector':
            case 'company':
                dispatch(getCastingDirectors());
                break;
        }
    }, [category, dispatch]);

    const Render = ({ item, index }: { item: ICastingDirector; index: number }) => {
        switch (category) {
            case 'castingDirector':
            case 'company':
                return <CastingDirectorCard mode="search" castingDirector={item} key={index} />;
            default:
                return <div />;
        }
    };

    useEffect(() => {
        getValue();
    }, [getValue]);

    useEffect(() => {
        if (initialValue) {
            setSelected(initialValue);
        }
    }, [initialValue]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: theme[themeStyle].selectableBorder,
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        width: '100%',
                        height: '48px',
                        alignItems: 'center',
                        backgroundColor: theme[themeStyle].form_fields,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            borderColor: theme[themeStyle].highlight,
                            boxShadow: `0 0 0 1px ${theme[themeStyle].highlight}20`,
                        },
                        '&:focus-within': {
                            borderColor: theme[themeStyle].highlight,
                            boxShadow: `0 0 0 2px ${theme[themeStyle].highlight}30`,
                        },
                    }}>
                    <Autocomplete
                        value={selected}
                        onChange={(_, newValue) => handleOnChange(newValue)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        options={value}
                        getOptionLabel={option => option.company_name}
                        filterOptions={(options, { inputValue }) =>
                            options.filter(option =>
                                option.company_name.toLowerCase().includes(inputValue.toLowerCase()),
                            )
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                    sx: {
                                        fontSize: '14px',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        '&::placeholder': {
                                            color: theme[themeStyle].placeholderGray,
                                            opacity: 1,
                                        },
                                    },
                                }}
                                sx={{
                                    px: 2,
                                    '& .MuiInputBase-root': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                style={{
                                    backgroundColor: theme[themeStyle].dropdown_black,
                                    borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                    padding: '12px 16px',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor = theme[themeStyle].role_hover;
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor = theme[themeStyle].dropdown_black;
                                }}>
                                <Render item={option} index={value.indexOf(option)} />
                            </li>
                        )}
                        renderTags={() =>
                            selected && (
                                <CastingDirectorCard
                                    castingDirector={selected}
                                    mode="display"
                                    onSelectionClick={handleCancelClick}
                                />
                            )
                        }
                        PaperComponent={({ children, ...props }) => (
                            <Paper
                                {...props}
                                sx={{
                                    backgroundColor: theme[themeStyle].dropdown_black,
                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                    borderRadius: 2,
                                    boxShadow: `0 8px 32px ${theme[themeStyle].shadowColor}`,
                                    mt: 1,
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    '& .MuiAutocomplete-listbox': {
                                        padding: 0,
                                        backgroundColor: 'transparent',
                                    },
                                    '& .MuiAutocomplete-option': {
                                        padding: 0,
                                    },
                                }}>
                                {children}
                            </Paper>
                        )}
                        sx={{
                            width: '100%',
                            '& .MuiAutocomplete-popupIndicator': {
                                color: theme[themeStyle].tag_white,
                            },
                            '& .MuiAutocomplete-clearIndicator': {
                                color: theme[themeStyle].tag_white,
                            },
                        }}
                    />
                </Box>
                <IconButton
                    onClick={onAddClick}
                    sx={{
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].true_white,
                        mx: 1,
                        width: 48,
                        height: 48,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].true_highlight,
                            transform: 'scale(1.05)',
                            boxShadow: `0 4px 12px ${theme[themeStyle].highlight}40`,
                        },
                    }}>
                    <AddIcon sx={{ color: theme[themeStyle].black }} />
                </IconButton>
            </Box>
            <Box mt={1}>
                {selected && (
                    <CastingDirectorCard
                        castingDirector={selected}
                        mode="display"
                        onSelectionClick={() => handleOnChange(null)}
                    />
                )}
            </Box>
        </Box>
    );
};

export default SearchSelectComponent;
